import { useState, useRef, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { destinations } from '@/data/destinations';

interface Props {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  label?: string;
  icon?: React.ReactNode;
}

const AddressAutocomplete = ({ value, onChange, placeholder, label, icon }: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const q = value.trim().toLowerCase();
  const suggestions = q
    ? destinations.filter(d => d.name.toLowerCase().includes(q)).slice(0, 8)
    : destinations.filter(d => d.popular).slice(0, 8);

  return (
    <div ref={ref} className="relative">
      {label && <label className="block text-xs font-semibold text-foreground mb-1.5">{label}</label>}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          {icon || <MapPin className="w-4 h-4" />}
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-10 pr-3 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/50 outline-none"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-xl shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((s) => (
            <button
              type="button"
              key={s.name}
              onClick={() => { onChange(s.name); setOpen(false); }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted flex items-center justify-between gap-2 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span>{s.flag}</span>
                <span className="text-foreground">{s.name}</span>
              </span>
              {s.km > 0 && <span className="text-xs text-muted-foreground">{s.km} km</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
